import React, { useState, useEffect } from 'react';
import { loginUser } from '../services/api';
import logo from '../assets/logo.png';
import logoEdu from '../assets/logoedu.png';
import shadesIndiaLogo from '../assets/shadesindialogo.png';

const Login = ({ onLoginSuccess }) => {
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    console.log('🔴 error state changed to:', JSON.stringify(error));
    console.trace();
  }, [error]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDismissError = () => setError('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🟡 handleSubmit fired, current error:', JSON.stringify(error));

    const username = formData.username.trim();
    const password = formData.password.trim();

    if (!username) { setError('Username is required'); return; }
    if (!password) { setError('Password is required'); return; }

    setLoading(true);
    console.log('🟡 setLoading(true), error still:', JSON.stringify(error));

    try {
      const response = await loginUser(username, password);
      console.log('📨 FULL RESPONSE:', JSON.stringify(response));
      console.log('📨 response.success:', response.success);

      if (response.success) {
        setError('');
        onLoginSuccess(response.data);
      } else {
        console.log('📨 Setting error now...');
        // Show the server's message (covers "already logged in",
        // "account deactivated", "invalid username or password", etc.)
        setError(response.message || 'Incorrect username or password');
      }
    } catch (err) {
      console.log('📨 CATCH BLOCK:', err);
      // If loginUser lets axios errors bubble up, the server's error
      // body (e.g. the 409 already-logged-in message) is here:
      const serverMsg = err.response?.data?.message;
      setError(serverMsg || err.message || 'Unable to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-5"
      style={{ background: '#F8FAFC' }}
    >
      <div className="bg-white rounded-xl w-full max-w-[440px] p-10 border border-slate-200"
        style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.08)' }}>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-1">
            <img src={logoEdu} alt="LTMS logo" className="h-16 object-contain flex-shrink-0" />
            <h1 className="text-[36px] font-bold m-0" style={{ color: '#2563EB' }}>
              LTMS
            </h1>
          </div>
          <p className="text-[14px] text-slate-500 m-0">
            Logistics and Transport Management System
          </p>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mb-5 animate-[fadeIn_0.15s_ease]">
            <div
              className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-300 border-l-4 border-l-red-600 rounded-lg"
              role="alert"
            >
              <div className="flex items-center gap-2.5 flex-1">
                <span className="text-base flex-shrink-0">⚠️</span>
                <div className="text-[14px] font-medium text-red-800 leading-snug">{error}</div>
              </div>
              <button
                type="button"
                onClick={handleDismissError}
                aria-label="Dismiss"
                className="bg-transparent border-0 text-red-700 text-lg leading-none cursor-pointer
                  px-1.5 py-0.5 rounded transition-colors duration-150 hover:bg-red-100 flex-shrink-0"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate autoComplete="on">

          {/* Username */}
          <div className="flex flex-col gap-2">
            <label htmlFor="username" className="text-[14px] font-semibold text-slate-700">
              Username <span className="text-red-600 ml-0.5">*</span>
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Enter your username"
              disabled={loading}
              autoComplete="username"
              autoFocus
              className="px-4 py-3.5 text-[15px] border-2 rounded-lg bg-white
                font-[inherit] transition-all duration-200 outline-none
                border-[#CBD5E1] focus:border-[#2563EB] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)]
                disabled:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-70"
            />
          </div>

          {/* Password */}
          <div className="flex flex-col gap-2">
            <label htmlFor="password" className="text-[14px] font-semibold text-slate-700">
              Password <span className="text-red-600 ml-0.5">*</span>
            </label>
            <div className="relative flex items-center">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter your password"
                disabled={loading}
                autoComplete="current-password"
                className="w-full px-4 py-3.5 pr-12 text-[15px] border-2 rounded-lg bg-white
                  font-[inherit] transition-all duration-200 outline-none
                  border-[#CBD5E1] focus:border-[#2563EB] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)]
                  disabled:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-70"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-2 bg-transparent border-0 cursor-pointer p-2 text-xl
                  flex items-center justify-center rounded-md transition-colors duration-150
                  hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50
                  select-none"
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading}
            className="mt-2 px-6 py-3.5 text-[16px] font-semibold border-0 rounded-lg
              cursor-pointer transition-colors duration-200 select-none
              disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
            style={{
              backgroundColor: loading ? '#2563EB' : '#2563EB',
              color: '#ffffff'
            }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#1D4ED8'; }}
            onMouseLeave={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#2563EB'; }}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2.5">
                <span
                  className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"
                />
                <span>Logging in...</span>
              </span>
            ) : 'Login'}
          </button>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-center gap-4 mt-3 pt-3 border-t border-slate-100">
          <img src={logo} alt="Partner logo" className="h-16 object-contain flex-shrink-0" />
          <img src={shadesIndiaLogo} alt="Shades India logo" className="h-16 object-contain flex-shrink-0" />
        </div>
              </div>

      {/* Debug panel — dev only */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 right-4 bg-slate-800 text-white p-4 rounded-xl text-xs
          font-mono min-w-[260px] z-[9999] border border-slate-700"
          style={{ boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
          <div className="text-[13px] font-bold mb-3 pb-2.5 border-b border-slate-700 text-amber-400 text-center">
            🐛 Debug Info
          </div>
          {[
            { label: 'Loading', value: loading ? '✓ TRUE' : '✗ FALSE', color: loading ? 'text-green-400' : 'text-slate-500' },
            { label: 'Error',   value: error || '(none)',               color: error   ? 'text-green-400' : 'text-slate-500' },
            { label: 'Username',value: formData.username || '(empty)',  color: 'text-blue-400' },
            { label: 'Password',value: formData.password ? `${formData.password.length} chars` : '(empty)', color: 'text-blue-400' },
          ].map(({ label, value, color }) => (
            <div key={label} className="flex justify-between py-1.5 gap-3 border-b border-slate-700/50">
              <span className="text-slate-400 min-w-[80px] flex-shrink-0">{label}:</span>
              <span className={`font-bold text-right flex-1 break-all ${color}`}>{value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Login;